import { z } from 'zod';
/**
 * Sites, users and roles (REST /api/v1). A client (e.g. a municipality) is a root site of kind
 * 'organization'; localities sit under a client and zones under a locality or another zone.
 * Every list is already filtered server-side to what the caller may see.
 */
export const SiteKind = z.enum(['organization', 'locality', 'zone', 'other']);
export const SiteStatus = z.enum(['active', 'archived']);
/**
 * Per-user command rate limit of client users (site administrator, operator): at most `max_actions` actions (a device
 * command, a scene run) in `window_minutes`. Global default in the operations settings, per-client override.
 */
export const CommandRateLimit = z.object({
    max_actions: z.number().int().min(1).max(1000),
    window_minutes: z.number().int().min(1).max(1440),
});
export const Site = z.object({
    id: z.uuid(),
    parent_id: z.uuid().nullable(),
    kind: SiteKind,
    name: z.string(),
    code: z.string().nullable(),
    time_zone: z.string(),
    status: SiteStatus,
    lon: z.number().nullable(),
    lat: z.number().nullable(),
    /** Client only: prefix of its accounts' usernames ("<prefix>.<name>", U13). */
    user_prefix: z.string().nullable(),
    /** Client only: devices reporting a position inside the drawn area are adopted automatically (U12). */
    auto_adopt: z.boolean(),
    /** A boundary (area) is drawn for this site. */
    has_area: z.boolean(),
    /** Client only: its own command rate limit for client users; null = the global default. */
    command_rate_limit: CommandRateLimit.nullable().optional(),
    /** Client only: administrative unit (UAT) the client is — commune / town name, county, SIRUTA code (INS). */
    uat_name: z.string().nullable().optional(),
    county: z.string().nullable().optional(),
    siruta_code: z.string().nullable().optional(),
    /** Devices assigned directly to this site (not descendants). */
    device_count: z.number().int().nonnegative(),
    /** Caller may rename/archive/delete this site. */
    can_manage: z.boolean(),
    /** Caller may create sites below this one. */
    can_add_child: z.boolean(),
    /** Caller may draw/change the area (client areas and auto-adopt: global site.manage only). */
    can_edit_area: z.boolean(),
});
export const SiteList = z.object({ items: z.array(Site), can_create_client: z.boolean() });
const Name = z.string().trim().min(1).max(120);
/** Username prefix of a client: lower-case letters and digits (U13). */
export const UserPrefix = z.string().regex(/^[a-z0-9]{2,32}$/);
/** Suggested prefix from a client name: "Valea Lupului" → "valealupului" (no spaces, no diacritics). */
export const userPrefixFromName = (name) => name
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .replace(/[^a-z0-9]/g, '')
    .slice(0, 28);
const Code = z.string().trim().max(40).nullable();
/** Romanian counties (județe) and Bucharest: the choices of a client's county (user request 2026-10-10). */
export const ROMANIAN_COUNTIES = [
    'Alba',
    'Arad',
    'Argeș',
    'Bacău',
    'Bihor',
    'Bistrița-Năsăud',
    'Botoșani',
    'Brașov',
    'Brăila',
    'București',
    'Buzău',
    'Caraș-Severin',
    'Călărași',
    'Cluj',
    'Constanța',
    'Covasna',
    'Dâmbovița',
    'Dolj',
    'Galați',
    'Giurgiu',
    'Gorj',
    'Harghita',
    'Hunedoara',
    'Ialomița',
    'Iași',
    'Ilfov',
    'Maramureș',
    'Mehedinți',
    'Mureș',
    'Neamț',
    'Olt',
    'Prahova',
    'Satu Mare',
    'Sălaj',
    'Sibiu',
    'Suceava',
    'Teleorman',
    'Timiș',
    'Tulcea',
    'Vaslui',
    'Vâlcea',
    'Vrancea',
];
export const County = z.enum(ROMANIAN_COUNTIES);
/** Time zone of a new site when none is given and there is no parent: the UATs are Romanian. */
export const DEFAULT_TIME_ZONE = 'Europe/Bucharest';
/** SIRUTA code of an administrative unit (INS): up to six digits. */
export const SirutaCode = z
    .string()
    .trim()
    .regex(/^[0-9]{1,6}$/);
const Coordinates = {
    lon: z.number().min(-180).max(180).nullable(),
    lat: z.number().min(-90).max(90).nullable(),
};
export const SiteCreate = z.object({
    parent_id: z.uuid().nullable(),
    kind: SiteKind,
    name: Name,
    code: Code.optional(),
    time_zone: z.string().min(1).max(64).optional(),
    /** Clients only; defaults to userPrefixFromName(name). */
    user_prefix: UserPrefix.optional(),
    lon: Coordinates.lon.optional(),
    lat: Coordinates.lat.optional(),
    /** clients only: administrative unit (UAT) */
    uat_name: z.string().trim().min(1).max(120).nullable().optional(),
    county: County.nullable().optional(),
    siruta_code: SirutaCode.nullable().optional(),
});
export const SiteUpdate = z
    .object({
    name: Name,
    code: Code,
    time_zone: z.string().min(1).max(64),
    status: SiteStatus,
    auto_adopt: z.boolean(),
    user_prefix: UserPrefix,
    /** clients only, global site.manage; null = back to the global default */
    command_rate_limit: CommandRateLimit.nullable(),
    /** clients only, global site.manage; null clears */
    uat_name: z.string().trim().min(1).max(120).nullable(),
    county: County.nullable(),
    siruta_code: SirutaCode.nullable(),
    ...Coordinates,
})
    .partial();
export const RoleScope = z.enum(['global', 'site']);
export const Role = z.object({
    key: z.string(),
    name_en: z.string(),
    scope: RoleScope,
    permissions: z.array(z.string()),
    /** Caller may grant this role somewhere. */
    grantable: z.boolean(),
});
export const RoleList = z.object({ items: z.array(Role) });
/**
 * A role on a site (null = global). `project_ids` (authority observer on a client only, U21): the account sees only
 * the devices of these projects of the client; absent or empty = the whole client.
 */
export const Assignment = z.object({
    role: z.string(),
    site_id: z.uuid().nullable(),
    project_ids: z.array(z.uuid()).max(500).optional(),
});
export const UserStatus = z.enum(['active', 'disabled']);
export const Locale = z.enum(['en', 'ro']);
export const User = z.object({
    id: z.uuid(),
    username: z.string(),
    display_name: z.string(),
    first_name: z.string().nullable(),
    last_name: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    status: UserStatus,
    locale: z.string(),
    assignments: z.array(Assignment.extend({
        site_name: z.string().nullable(),
        /** the projects the assignment is limited to (empty = the whole client) */
        projects: z.array(z.object({ id: z.uuid(), name: z.string() })),
    })),
    created_at: z.string(),
    last_login_at: z.string().nullable(),
    /** Paired phone (mobile PWA) and outstanding pairing PIN, if any. */
    mobile: z.object({
        device: z
            .object({ label: z.string().nullable(), enrolled_at: z.string(), last_seen_at: z.string().nullable() })
            .nullable(),
        pin_expires_at: z.string().nullable(),
    }),
    /** Caller may edit/disable/delete this account. */
    can_manage: z.boolean(),
});
export const UserList = z.object({ items: z.array(User) });
/** Lower-case letters, digits and . _ - (3–64). Usernames are case-insensitive. */
export const Username = z
    .string()
    .trim()
    .toLowerCase()
    .regex(/^[a-z0-9][a-z0-9._-]{2,63}$/);
/** Minimum length only (NIST SP 800-63B); no composition rules. */
export const NewPassword = z.string().min(12).max(1024);
/** POST /api/v1/auth/password — the signed-in user changes their own password (the current one is required). */
export const ChangeOwnPassword = z.object({
    current_password: z.string().min(1).max(1024),
    new_password: NewPassword,
});
/** Phone: digits with optional leading + and common separators. */
export const Phone = z
    .string()
    .trim()
    .regex(/^\+?[0-9][0-9 ()./-]{5,24}$/);
const PersonName = z.string().trim().min(1).max(60);
const hasContact = (b) => Boolean(b.email || b.phone);
/**
 * Client accounts (SITE_ADMIN, OPERATOR) hold exactly one role on one client and their username is
 * "<client user_prefix>.<name>"; global accounts hold global roles and any username (U13).
 * At least one contact (email or phone) is required.
 */
export const UserCreate = z
    .object({
    username: Username,
    first_name: PersonName,
    last_name: PersonName,
    email: z.email().max(254).nullable().optional(),
    phone: Phone.nullable().optional(),
    locale: Locale.optional(),
    password: NewPassword,
    assignments: z.array(Assignment).min(1).max(20),
})
    .refine(hasContact, { message: 'email or phone required', path: ['contact'] });
export const UserUpdate = z
    .object({
    first_name: PersonName,
    last_name: PersonName,
    email: z.email().max(254).nullable(),
    phone: Phone.nullable(),
    locale: Locale,
    status: UserStatus,
    password: NewPassword,
    assignments: z.array(Assignment).min(1).max(20),
})
    .partial();
/** POST /api/v1/users/:id/mobile/pin — a new one-time pairing PIN (shown once). */
export const PairingPinRequest = z.object({ reset_device: z.boolean().optional() });
export const PairingPin = z.object({ pin: z.string().regex(/^\d{8}$/), expires_at: z.string() });
/** Public ECDSA P-256 key as JWK (the private key stays non-extractable on the phone). */
export const DevicePublicKey = z.object({
    kty: z.literal('EC'),
    crv: z.literal('P-256'),
    x: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
    y: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
});
/** POST /api/v1/mobile/pair — username + one-time PIN + the phone's public key. */
export const MobilePair = z.object({
    username: z.string().trim().toLowerCase().min(1).max(64),
    pin: z.string().regex(/^\d{8}$/),
    public_key: DevicePublicKey,
    label: z.string().trim().max(80).optional(),
});
export const MobileChallengeRequest = z.object({ device_id: z.uuid() });
export const MobileChallenge = z.object({ nonce: z.string() });
/** POST /api/v1/mobile/session — signature (ECDSA P-256/SHA-256, IEEE P1363, base64url) over mobileSigningInput(). */
export const MobileSessionRequest = z.object({
    device_id: z.uuid(),
    nonce: z.string().min(16).max(128),
    signature: z.string().regex(/^[A-Za-z0-9_-]{86}$/),
});
/** Exact bytes signed by the phone (domain-separated so the key signs nothing else). */
export const mobileSigningInput = (deviceId, nonce) => `udm-mobile-session:v1:${deviceId}:${nonce}`;
/** Header (REST) / query parameter (WebSocket) selecting the mobile session cookie. */
export const MOBILE_CLIENT_HEADER = 'x-udm-client';
// ---- site areas (U12) ----
const Position = z.tuple([z.number().min(-180).max(180), z.number().min(-90).max(90)]);
const Ring = z.array(Position).min(4).max(20_000);
export const AreaGeometry = z.discriminatedUnion('type', [
    z.object({ type: z.literal('Polygon'), coordinates: z.array(Ring).min(1).max(100) }),
    z.object({
        type: z.literal('MultiPolygon'),
        coordinates: z.array(z.array(Ring).min(1).max(100)).min(1).max(200),
    }),
]);
/** PUT /api/v1/sites/:id/area — null removes the area. Self-intersections are repaired server-side. */
export const SiteAreaUpdate = z.object({ geometry: AreaGeometry.nullable() });
/**
 * POST /api/v1/sites/:id/area/check — where a drawn area overlaps the areas of the site's siblings (other clients for a
 * client, the other sites under the same parent otherwise). Saving such an area is refused with AREA_OVERLAP.
 */
export const SiteAreaOverlap = z.object({
    site_id: z.uuid(),
    name: z.string(),
    /** overlapping surface, m² */
    area_m2: z.number(),
    /** the overlapping part itself */
    geometry: AreaGeometry,
});
export const SiteAreaCheck = z.object({ overlaps: z.array(SiteAreaOverlap) });
/**
 * POST /api/v1/sites/:id/area/match-border — the drawn area pulled back to the border of the given sibling areas
 * (their parts removed, a few centimetres of clearance), so it no longer overlaps them. null = nothing left.
 */
export const SiteAreaMatchBorder = z.object({
    geometry: AreaGeometry,
    site_ids: z.array(z.uuid()).min(1).max(20),
});
/**
 * POST /api/v1/sites/from-proposal — a client (or a locality of a client) for a proposed commune, in ONE transaction:
 * the site, its area (the commune's exact boundary from the cached administrative areas, not the simplified map
 * geometry) and, with `adopt`, automatic adoption (for a locality the client's area is extended by the commune). Thin
 * overlaps with neighbouring areas (simplified or hand-drawn borders of the same communes) are trimmed from the new
 * area and reported; a real overlap (more than a sliver) is refused with AREA_OVERLAP and nothing is created.
 */
export const SiteFromProposal = z
    .object({
    osm_ref: z.string().min(1).max(64),
    mode: z.enum(['client', 'locality']),
    /** the client, for mode "locality" */
    parent_id: z.uuid().nullable().optional(),
    name: Name,
    /** clients only; defaults to userPrefixFromName(name) */
    user_prefix: UserPrefix.optional(),
    adopt: z.boolean(),
})
    .refine((b) => (b.mode === 'locality') === !!b.parent_id, { path: ['parent_id'] });
export const SiteFromProposalResult = z.object({
    site: Site,
    /** surfaces removed from the commune's boundary because a neighbour already covers them */
    trimmed: z.array(z.object({ site_id: z.uuid(), name: z.string(), area_m2: z.number() })),
});
/** GET /api/v1/sites/areas — areas of all visible sites (GeoJSON FeatureCollection, MultiPolygon geometries). */
export const SiteAreaFeature = z.object({
    type: z.literal('Feature'),
    id: z.uuid(),
    properties: z.object({
        id: z.uuid(),
        name: z.string(),
        kind: SiteKind,
        parent_id: z.uuid().nullable(),
        client_id: z.uuid(),
        auto_adopt: z.boolean(),
    }),
    geometry: z.object({ type: z.literal('MultiPolygon'), coordinates: z.array(z.array(z.array(Position))) }),
});
export const SiteAreas = z.object({
    type: z.literal('FeatureCollection'),
    features: z.array(SiteAreaFeature),
});
/**
 * An OpenStreetMap administrative area offered as a site boundary (commune, town, city). Reference data from Nominatim,
 * cached by Central; the geometry is simplified (≈ 10 m) so the editor can edit it.
 */
export const AdminArea = z.object({
    /** OpenStreetMap reference, e.g. "relation/123456" */
    osm_ref: z.string(),
    name: z.string(),
    county: z.string().nullable(),
    /** Nominatim address type: municipality, town, city, village… */
    kind: z.string().nullable(),
    /** [west, south, east, north] */
    bbox: z.tuple([z.number(), z.number(), z.number(), z.number()]),
    geometry: AreaGeometry,
});
/** GET /api/v1/admin-areas/search?q= — site.manage anywhere. `enabled` false when Central has no Nominatim URL. */
export const AdminAreaSearch = z.object({ enabled: z.boolean(), items: z.array(AdminArea) });
/** GET /api/v1/admin-areas/at?lon=&lat= — the commune containing the point, or null. */
export const AdminAreaAt = z.object({ enabled: z.boolean(), area: AdminArea.nullable() });
/**
 * Device placement review (after an area change): active devices whose position lies in another site's area
 * (`other_area`, with the target), outside every area of their client (`outside_areas`) or inside two clients'
 * areas (`ambiguous`). `movable` = may be moved to the proposed target by the caller: devices adopted automatically
 * from their GPS position (site managers of both sites), and, for a superadmin, imported or manually adopted ones
 * too (U17, amended 2026-10-08: only when selected explicitly; nothing is preselected).
 */
export const PlacementSite = z.object({
    site_id: z.uuid(),
    /** names from the client down to the site */
    path: z.array(z.string()),
});
export const PlacementItem = z.object({
    device_id: z.uuid(),
    name: z.string(),
    lon: z.number(),
    lat: z.number(),
    /** how the device got its site: GPS auto-adoption, manual adoption, legacy import, other */
    source: z.enum(['auto_gps', 'manual', 'import', 'other']),
    reason: z.enum(['other_area', 'outside_areas', 'ambiguous']),
    current: PlacementSite,
    target: PlacementSite.nullable(),
    movable: z.boolean(),
});
/**
 * GET /api/v1/sites/placement-review?site_id= — site.manage there (or global device.provision). Without site_id: the
 * whole platform, superadmin only (global device.provision and site.manage).
 */
export const PlacementReview = z.object({ items: z.array(PlacementItem) });
/**
 * POST /api/v1/sites/placement-review/apply — every device is re-checked server-side before it moves. Without
 * site_id each device moves to the target its position resolves to; with site_id (superadmin only) the listed
 * active devices move to that site whatever their position (the operator's explicit choice).
 */
export const PlacementApply = z.object({
    device_ids: z.array(z.uuid()).min(1).max(2000),
    site_id: z.uuid().optional(),
});
export const PlacementApplyResult = z.object({
    moved: z.array(z.uuid()),
    skipped: z.array(z.object({
        device_id: z.uuid(),
        reason: z.enum(['not_misplaced', 'not_auto_adopted', 'forbidden', 'changed']),
    })),
});
/**
 * POST /api/v1/sites/placement-review/unassign — superadmin only: the listed active devices leave their site and go
 * back to Discovered (no site; auto-adoption may adopt them again at their next connection). A device that other
 * devices depend on (a concentrator) is skipped.
 */
export const PlacementUnassign = z.object({ device_ids: z.array(z.uuid()).min(1).max(2000) });
export const PlacementUnassignResult = z.object({
    unassigned: z.array(z.uuid()),
    skipped: z.array(z.object({ device_id: z.uuid(), reason: z.enum(['changed', 'has_dependants']) })),
});
/** GET /api/v1/sites/adoption-preview?lon=&lat= — where a device at this position would be adopted. */
export const AdoptionPreview = z.object({
    site_id: z.uuid().nullable(),
    /** Names from the client down to the adopting site. */
    path: z.array(z.string()),
});
/** User manuals (docs/manuals, superadmin only): one entry per manual and language, with the formats on disk. */
export const ManualList = z.object({
    items: z.array(z.object({
        lang: z.string(),
        slug: z.string(),
        title: z.string(),
        formats: z.array(z.enum(['html', 'pdf', 'docx'])),
        updated_at: z.string(),
    })),
});
//# sourceMappingURL=admin.js.map